import React, { useContext, useEffect, useState } from 'react';
import { UserRoleContext } from '../context/UserRoleContext';
import { EventContext } from '../context/EventContext';
import { Message, CreateMessage } from '../types/Message';
import { Group } from '../types/Group';
import { getMessagesForEvent, getMessagesForGroup, createGroupMessage, createEventMessage, getGroupMessagesForEvent, sendAssignmentMessages } from '../services/messageService';
import { getGroupsByEventId } from '../services/groupService';
import { useToast } from '../context/ToastContext';
import { ROLES } from '../constants/roles';

export const MessageComponent: React.FC = () => {
  const { userRole } = useContext(UserRoleContext);
  const { currentEvent } = useContext(EventContext);

  const [eventMessages, setEventMessages] = useState<Message[]>([]);
  const [groupMessages, setGroupMessages] = useState<Message[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [newMessageText, setNewMessageText] = useState<string>('');
  const [messageCreationError, setMessageCreationError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState<boolean>(false); // Acknowledgment checkbox state
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [sendText, setSendText] = useState<boolean>(true);
  const [sendAssignmentDisabled, setSendAssignmentDisabled] = useState<boolean>(false);

  const handleChange = (value: "email" | "text" | "both") => {
    setSendEmail(value === "email" || value === "both");
    setSendText(value === "text" || value === "both");
  };

  const { addToast } = useToast();

  const recipientOptions = ['invited', 'not_invited', 'checked_in', 'checked_in', 'attending', 'not_attending', 'all'];

  // Fetch event and group messages on event change
  useEffect(() => {
    const fetchMessages = async () => {
      if (currentEvent) {
        try {
          const eventMsgs = await getMessagesForEvent(currentEvent.event_id);
          setEventMessages(eventMsgs);

          const groupMessages = await getGroupMessagesForEvent(currentEvent.event_id);
          setGroupMessages(groupMessages);

          const eventGroups = await getGroupsByEventId(currentEvent.event_id);
          setGroups(eventGroups);
        } catch (error) {
          addToast('Error fetching message', true);
        }
      }
    };

    fetchMessages();
  }, [currentEvent, selectedGroupId]);

  const handleCreateMessage = async () => {
    if (!newMessageText) {
      setMessageCreationError('Message text cannot be empty.');
      return;
    }
    if (!acknowledged) {
      setMessageCreationError('You must acknowledge the message limit before sending.');
      return;
    }
    setMessageCreationError(null);
    const message: CreateMessage = { message_text: newMessageText, event_id: currentEvent?.event_id, is_email: sendEmail, is_text: sendText };
    try {
      if (selectedGroupId && !recipientOptions.includes(selectedGroupId)) {
        // Create group message
        message.event_group_id = selectedGroupId;
        const newMessage = await createGroupMessage(selectedGroupId, message);
        addToast("Message sent");
      } else if (currentEvent) {
        // Create event-wide message

        const newMessage = await createEventMessage(currentEvent.event_id, { message_text: newMessageText, event_id: currentEvent.event_id, is_email: sendEmail, is_text: sendText, message_filter: selectedGroupId ?? 'checked_in' });
        if (newMessage) {
          setEventMessages((prevMessages) => [...prevMessages, newMessage]);
        }
        addToast("Message sent");
      } else {
        setMessageCreationError(`Please select a group or choose to send the message to the entire event.`);
        return;
      }
    } catch (error) {
      setMessageCreationError('Failed to create message.');
      addToast("Failed to create message", true);
    }

    setSelectedGroupId(null);

    setNewMessageText('');
    setAcknowledged(false);
  };

  const sendAssignments = async () => {

    setSendAssignmentDisabled(true);
    addToast("Sending assignments, please wait.");
    setMessageCreationError(null);

    if (currentEvent) {
      try {
        const success = await sendAssignmentMessages(currentEvent?.event_id);
        if (success) {
          addToast("Assignments sent to registrants!");
        } else {
          addToast("Error sending assignments to registrants!", true);
          setSendAssignmentDisabled(false);
          setMessageCreationError("Server error, please try again later.");

        }
      } catch (error) {
        addToast("Error sending assignments to registrants!", true);
        setSendAssignmentDisabled(false);
        setMessageCreationError("Server error, please try again later.");

      }
    } else {
      addToast("Error sending assignments to registrants!", true);
      setSendAssignmentDisabled(false);
      setMessageCreationError("No Event Set!");
    }
  }

  const isSendDisabled = !newMessageText || !acknowledged || !selectedGroupId;

  return (
    <div>
      {(userRole?.role_id === ROLES.ADMIN || userRole?.role_id === ROLES.MANAGER) && userRole.host_id == currentEvent?.host_id && (
        <div className='card'>
          <h2>Create a New Message</h2>
          <div>
            <select onChange={(e) => setSelectedGroupId(e.target.value)} value={selectedGroupId || ''} disabled={currentEvent?.is_locked}>
              <option value="" disabled>Select Message Recipient</option>
              {currentEvent?.has_invitation && <option value="invited">Send to Invited Registrants</option>}
              {currentEvent?.has_invitation && <option value="not_invited">Send to Not Invited Registrants</option>}
              {/* {currentEvent?.has_invitation && <option value="attending">Send to Attending Registrants</option>} */}
              {/* {currentEvent?.has_invitation && <option value="not_attending">Send to Not Attending Registrants</option>} */}
              {currentEvent?.has_checkin && <option value="not_checked_in">Send to Not Checked In Registrants</option>}

              <option value="checked_in">Send to Checked In Registrants</option>
              <option value="all">Send to Entire Event (All Registrants)</option>

              {groups.map((group) => (
                <option key={group.event_group_id} value={group.event_group_id}>
                  {`${currentEvent?.event_type_group_name}: ${group.group_name}`}
                </option>
              ))}
            </select>
            <label>
              <input style={{ width: '20px' }}
                type="radio"
                name="notification"
                value="both"
                checked={sendEmail && sendText}
                onChange={() => handleChange("both")}
                disabled={currentEvent?.is_locked}
              />
              Email and Text
            </label>
            <label>
              <input style={{ width: '20px' }}
                type="radio"
                name="notification"
                value="email"
                checked={sendEmail && !sendText}
                onChange={() => handleChange("email")}
                disabled={currentEvent?.is_locked}
              />
              Email
            </label>
            <label>
              <input style={{ width: '20px' }}
                type="radio"
                name="notification"
                value="text"
                checked={!sendEmail && sendText}
                onChange={() => handleChange("text")}
                disabled={currentEvent?.is_locked}
              />
              Text
            </label>
          </div>
          <textarea
            value={newMessageText}
            onChange={(e) => setNewMessageText(e.target.value)}
            placeholder="Type your message here..."
            style={{ height: '100px', width: '80%', boxShadow: '0 5px 8px rgba(0, 0, 0, 0.2)', maxHeight: '400px', maxWidth: '800px' }}
            disabled={currentEvent?.is_locked}
            maxLength={140}
          />



          <div>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={() => setAcknowledged(!acknowledged)}
              disabled={currentEvent?.is_locked}
            />
            <label>
              I acknowledge that I am personally responsible for the content sent in this messages and adhere to the <a href="https://cyneup.com/privacy-policy" target="_blank">Privacy Policy</a> and <a href="https://cyneup.com/terms-of-service" target="_blank">Terms of Service</a>.
            </label>
          </div>

          <button onClick={handleCreateMessage} disabled={isSendDisabled}>Send Message</button>
          {messageCreationError && <p style={{ color: 'red' }}>{messageCreationError}</p>}
          { currentEvent?.event_type_id === 'MUSIC' && (<div>
          <br />
          <br />

          <button onClick={sendAssignments}
            disabled={sendAssignmentDisabled || !acknowledged || currentEvent?.is_locked}
            style={{ maxWidth: '100%', whiteSpace: 'wrap' }}
          >{`Send ${currentEvent?.event_type_group_name} Assignments ${currentEvent?.has_judging ? 'and Chair Numbers' : ''} To Registrants`}
          </button>
          <p><strong>Example Message: </strong><i>{`Message From Cyne Up: Your Group Assignment for ${currentEvent?.event_name ?? 'Test Event'} is ${currentEvent?.event_type_group_name} 'Alpha' ${currentEvent?.has_judging ? ` chair 2.` : '.'}`}</i></p>
          </div>)}
        </div>

      )}
      <h2>Sent Event Messages</h2>
      <table className='tableWrapper'>
        <thead>
          <tr>
            <th>Sender</th>
            <th>Message</th>
            <th>Date</th>
            <th>Filter</th>
          </tr>
        </thead>
        <tbody>
          {eventMessages.map((message) => (<tr key={message.message_id}><td>{message.sender_first_name} {message.sender_last_name}</td>
            <td>{message.message_text}</td>
            <td>{new Date(message.created_at).toLocaleString()}</td>
            <td>{message.message_filter}</td></tr>))}
        </tbody>
      </table>

      <h2>Sent {currentEvent?.event_type_group_name} Messages</h2>
      <div className='tableWrapper'>
        <table>
          <thead>
            <tr><th>{currentEvent?.event_type_group_name} Name</th><th>Sender</th>
              <th>Message</th>
              <th>Date</th></tr>
          </thead>
          <tbody>
            {groupMessages.map((message) => {
              // Find the group name from the event_group_id
              const groupName = groups.find(group => group.event_group_id == message.event_group_id)?.group_name || 'Unknown Group';
              return (<tr key={message.message_id}><td>{groupName}</td>
                <td>{message.sender_first_name} {message.sender_last_name}</td>
                <td>{message.message_text}</td>
                <td>{new Date(message.created_at).toLocaleString()}</td></tr>);
            })}
          </tbody>
        </table>
      </div>


    </div>
  );
};

export default MessageComponent;
